import { Users, Clock, TrendingUp, CalendarCheck2, Hand } from 'lucide-react';
import { useDashboardStats, usePendingLeaves } from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageTransition, FadeIn, StaggerList, StaggerItem } from '@/components/ui/motion';

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useDashboardStats();
  const { data: pending } = usePendingLeaves();

  const kpis = [
    {
      label: 'Headcount total',
      value: data?.totalEmployees ?? 0,
      icon: Users,
      iconBg: 'bg-primary-subtle',
      iconColor: 'text-primary',
      trend: '+3',
      trendColor: 'text-accent',
    },
    {
      label: 'Ausencias hoy',
      value: data?.onLeaveToday?.length ?? 0,
      icon: CalendarCheck2,
      iconBg: 'bg-red-50',
      iconColor: 'text-error',
      trend: '2%',
      trendColor: 'text-font-secondary',
    },
    {
      label: 'Solicitudes pendientes',
      value: data?.pendingLeaves ?? 0,
      icon: Clock,
      iconBg: 'bg-yellow-50',
      iconColor: 'text-warning',
      trend: 'Revisar',
      trendColor: 'text-warning',
    },
    {
      label: 'Nuevos este mes',
      value: data?.approvedThisMonth ?? 0,
      icon: TrendingUp,
      iconBg: 'bg-accent-light',
      iconColor: 'text-accent',
      trend: '+12%',
      trendColor: 'text-accent',
    },
  ];

  // Bar chart data
  const byCountry: { code: string; count: number }[] = (data?.employeesByCountry ?? [])
    .map((c: any) => ({
      code: c.code ?? c.countryCode ?? 'XX',
      count: c.count ?? c._count?._all ?? 0,
    }))
    .sort((a: any, b: any) => b.count - a.count);
  const maxCount = Math.max(1, ...byCountry.map((c) => c.count));

  return (
    <PageTransition className="flex flex-col gap-6">
      <FadeIn>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-font-primary">
          Hola, {user?.fullName?.split(' ')[0] ?? 'Admin'}
          <Hand size={22} className="text-primary-light" />
        </h1>
        <p className="mt-1 text-sm text-font-secondary">
          Bienvenido de vuelta. Este es el resumen de tu equipo.
        </p>
      </FadeIn>

      {/* KPI cards */}
      <StaggerList className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <StaggerItem key={k.label}>
            <Card hover className="h-full rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${k.iconBg}`}
                >
                  <k.icon size={18} className={k.iconColor} />
                </div>
                <span className={`text-xs font-semibold ${k.trendColor}`}>{k.trend}</span>
              </div>
              <p className="mt-4 text-3xl font-bold text-font-primary">
                {isLoading ? '...' : k.value}
              </p>
              <p className="mt-1 text-xs font-medium text-font-secondary">{k.label}</p>
            </Card>
          </StaggerItem>
        ))}
      </StaggerList>

      {/* Chart + pending */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <FadeIn delay={0.05} className="lg:col-span-2">
          <Card hover className="h-full rounded-xl p-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-y-2">
              <h2 className="text-base font-semibold text-font-primary">
                Colaboradores por país
              </h2>
              <Button variant="ghost" size="sm">
                Ver detalle
              </Button>
            </div>

            {byCountry.length ? (
              <div className="flex h-64 items-end justify-around gap-4 px-2">
                {byCountry.map((c, i) => {
                  const height = (c.count / maxCount) * 100;
                  const color = i === 0 ? 'bg-primary' : 'bg-primary-light';
                  return (
                    <div key={c.code} className="flex flex-1 flex-col items-center gap-2">
                      <span className="text-xs font-semibold text-font-primary">
                        {c.count}
                      </span>
                      <div
                        className={`w-full rounded-t-md ${color} transition-all`}
                        style={{ height: `${height}%`, minHeight: '4px' }}
                      />
                      <span className="text-xs font-medium text-font-secondary">
                        {c.code}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center text-sm text-font-tertiary">
                Sin datos aún
              </div>
            )}
          </Card>
        </FadeIn>

        <FadeIn delay={0.1}>
          <Card hover className="h-full rounded-xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-font-primary">
                Aprobaciones pendientes
              </h2>
              {pending && pending.items?.length > 0 && (
                <Badge variant="warning">{pending.items.length}</Badge>
              )}
            </div>
            {pending?.items?.length ? (
              <ul className="flex flex-col gap-3">
                {pending.items.slice(0, 5).map((lr: any) => {
                  const name = `${lr.employee.firstName} ${lr.employee.lastName}`;
                  return (
                    <li
                      key={lr.id}
                      className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-bg"
                    >
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-light to-primary text-[10px] font-semibold text-white">
                        {initials(name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-font-primary">
                          {name}
                        </p>
                        <p className="text-xs text-font-secondary">
                          {lr.type} · {lr.workingDays} día(s)
                        </p>
                      </div>
                      <Badge variant="warning" dot>
                        Pendiente
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="py-8 text-center text-xs text-font-secondary">
                No hay solicitudes pendientes
              </p>
            )}
          </Card>
        </FadeIn>
      </div>
    </PageTransition>
  );
}
